import * as ClickedConverter from './SingleFilterConverter/ClickedConverter';
import * as ErrorConverter from './SingleFilterConverter/ErrorConverter';
import * as BrowserConverter from './SingleFilterConverter/BrowserConverter';
import * as DateRangeConverter from './SingleFilterConverter/DateRangeConverter';
import * as DeviceTypeConverter from './SingleFilterConverter/DeviceTypeConverter';
import * as FeedbackConverter from './SingleFilterConverter/FeedbackConverter';
import * as ExperimentConverter from './SingleFilterConverter/ExperimentConverter';
import * as LargeStringListConverter from './SingleFilterConverter/GenericConverter/LargeStringListConverter';
import * as NewReturningUserConverter from './SingleFilterConverter/NewReturningUserConverter';
import * as NPSConverter from './SingleFilterConverter/NPSConverter';
import * as OperatingSystemConverter from './SingleFilterConverter/OperatingSystemConverter';
import * as NumberConverter from './SingleFilterConverter/GenericConverter/RadioNumberConverter';
import * as RageClickConverter from './SingleFilterConverter/RageClickConverter';
import * as SessionDurationConverter from './SingleFilterConverter/SessionDurationConverter';
import * as URLConverter from './SingleFilterConverter/URLConverter';
import * as UserIdConverter from './SingleFilterConverter/UserIdConverter';
import * as UserAttributeConverter from './SingleFilterConverter/UserAttributeConverter';
import * as UTurnConverter from './SingleFilterConverter/UTurnConverter';
import * as TeamCommentConverter from './SingleFilterConverter/TeamCommentConverter';

import type {
  QueryFormatFilter,
  QueryMapFilter,
} from '../../domains/global-filters/model/QueryFormat';
import { QueryKeyEnum, QueryListOperandEnum } from '../../domains/global-filters/model/QueryFormat';
import type {
  FilterType,
  FunnelFilter,
  GlobalFilter,
} from '../../domains/global-filters/model/FilterChain';
import { FilterKindEnum } from '../../domains/global-filters/model/FilterChain';
import type { ClickedType } from '../../components/StatelessComponent/SingleFilter/ClickedFilter/ClickedFilterType';
import type { ErrorType } from '../../components/StatelessComponent/SingleFilter/ErrorFilter/ErrorFilterType';
import type { NEW_RETURNING_USER_TYPE } from '../../components/StatelessComponent/SingleFilter/NewReturningUserFilter/NewReturningUserFilterType';
import type { RAGE_CLICK_TYPE } from '../../components/StatelessComponent/SingleFilter/RageClickFilter/RageClickFilterType';
import type { U_TURN_TYPE } from '../../components/StatelessComponent/SingleFilter/UTurnFilter/UTurnFilterType';
import type { TEAM_COMMENT_TYPE } from '../../components/StatelessComponent/SingleFilter/TeamCommentFilter/TeamCommentFilterType';

const shouldThrowError = (never: never): never => {
  throw new Error(`Unknown type: ${never}`);
};

function convertFromFilterTypeToQueryMapFilter(currentValue: FilterType[]): QueryMapFilter[] {
  const queryList: QueryMapFilter[] = [];

  const funnelFromUiToQueryFormat = (filter: FunnelFilter): QueryFormatFilter => {
    let steps =
      filter.value !== undefined ? convertFromFilterTypeToQueryMapFilter(filter.value?.steps) : [];

    if (filter.value && filter.value.withDropoff === true) {
      const lastStep = steps.pop();
      steps = [...steps, { DROPOFF: lastStep }];
    }

    return { [QueryKeyEnum.funnel]: steps };
  };

  currentValue.forEach((filter) => {
    let subQuery: QueryFormatFilter | undefined;

    switch (filter.kind) {
      case FilterKindEnum.Clicked: {
        subQuery = ClickedConverter.fromUiToQueryFormat(filter.value as ClickedType);
        break;
      }
      case FilterKindEnum.Error: {
        subQuery = ErrorConverter.fromUiToQueryFormat(filter.value as ErrorType);
        break;
      }
      case FilterKindEnum.NewReturningUserKind: {
        subQuery = NewReturningUserConverter.fromUiToQueryFormat(
          filter.value as NEW_RETURNING_USER_TYPE,
        );
        break;
      }
      case FilterKindEnum.RageClickKind: {
        subQuery = RageClickConverter.fromUiToQueryFormat(filter.value as RAGE_CLICK_TYPE);
        break;
      }
      case FilterKindEnum.UTurnKind: {
        subQuery = UTurnConverter.fromUiToQueryFormat(filter.value as U_TURN_TYPE);
        break;
      }
      case FilterKindEnum.TeamComment: {
        subQuery = TeamCommentConverter.fromUiToQueryFormat(filter.value as TEAM_COMMENT_TYPE);
        break;
      }
      case FilterKindEnum.DeviceType: {
        subQuery = DeviceTypeConverter.fromUiToQueryFormat(filter.value);
        break;
      }
      case FilterKindEnum.OS: {
        subQuery = OperatingSystemConverter.fromUiToQueryFormat(filter.value);
        break;
      }
      case FilterKindEnum.VisitedURL: {
        subQuery = URLConverter.fromUiToQueryFormat(filter.value, QueryKeyEnum.all_page_paths);
        break;
      }
      case FilterKindEnum.LandingURL: {
        subQuery = URLConverter.fromUiToQueryFormat(filter.value, QueryKeyEnum.first_page_path);
        break;
      }
      case FilterKindEnum.ExitURL: {
        subQuery = URLConverter.fromUiToQueryFormat(filter.value, QueryKeyEnum.last_page_path);
        break;
      }
      case FilterKindEnum.ReferrerURL: {
        subQuery = URLConverter.fromUiToQueryFormat(filter.value, QueryKeyEnum.referrer);
        break;
      }
      case FilterKindEnum.Browser: {
        subQuery = BrowserConverter.fromUiToQueryFormat(filter.value);
        break;
      }
      case FilterKindEnum.IncomingFeedback: {
        subQuery = FeedbackConverter.fromUiToQueryFormat(filter.value);
        break;
      }
      case FilterKindEnum.NPS: {
        subQuery = NPSConverter.fromUiToQueryFormat(filter.value);
        break;
      }
      case FilterKindEnum.UserID: {
        subQuery = UserIdConverter.fromUiToQueryFormat(filter.value);
        break;
      }
      case FilterKindEnum.PageCount: {
        subQuery = NumberConverter.fromUiToQueryFormat(filter.value, QueryKeyEnum.page_count);
        break;
      }
      case FilterKindEnum.SessionDuration: {
        subQuery = SessionDurationConverter.fromUiToQueryFormat(filter.value);
        break;
      }
      case FilterKindEnum.Country: {
        subQuery = LargeStringListConverter.fromUiToQueryFormat(
          filter.value,
          QueryKeyEnum.country_code,
        );
        break;
      }
      case FilterKindEnum.Event: {
        subQuery = LargeStringListConverter.fromUiToQueryFormat(
          filter.value,
          QueryKeyEnum.javascript_tag_names,
        );
        break;
      }
      case FilterKindEnum.GoogleOptimize: {
        subQuery = ExperimentConverter.fromUiToQueryFormat(filter.value, filter.kind);
        break;
      }
      case FilterKindEnum.Optimizely: {
        subQuery = ExperimentConverter.fromUiToQueryFormat(filter.value, filter.kind);
        break;
      }
      case FilterKindEnum.Unbounce: {
        subQuery = ExperimentConverter.fromUiToQueryFormat(filter.value, filter.kind);
        break;
      }
      case FilterKindEnum.UserAttribute: {
        subQuery = UserAttributeConverter.fromUiToQueryFormat(filter.value);
        break;
      }
      case FilterKindEnum.Funnel: {
        subQuery = funnelFromUiToQueryFormat(filter);
        break;
      }
      case FilterKindEnum.PageURL:
      case FilterKindEnum.SurveyCompletionKind:
      case FilterKindEnum.Keyword: {
        // We ignore those;
        break;
      }
      default: {
        shouldThrowError(filter);
      }
    }
    if (Array.isArray(subQuery)) {
      subQuery.forEach((entry) => {
        queryList.push(entry);
      });
    } else if (subQuery) {
      queryList.push(subQuery as QueryMapFilter);
    }
  });

  return queryList;
}

export function convertFromFilterTypeToQueryFormat(currentValue: FilterType[]): QueryFormatFilter {
  const queryList = convertFromFilterTypeToQueryMapFilter(currentValue);

  if (queryList.length === 0) {
    return {};
  }
  if (queryList.length === 1) {
    return queryList[0] as QueryFormatFilter;
  }
  return { [QueryListOperandEnum.AND]: queryList as QueryMapFilter[] };
}

export function convertFromNewUiToQueryFormat(currentValue: GlobalFilter): QueryFormatFilter {
  const queryList: QueryMapFilter[] = [];

  if (currentValue.dateRange) {
    const subQuery = DateRangeConverter.fromUiToQueryFormat(currentValue.dateRange);
    queryList.push(subQuery);
  }

  queryList.push(...convertFromFilterTypeToQueryMapFilter(currentValue.filterList));

  if (queryList.length === 0) {
    return {};
  }
  if (queryList.length === 1) {
    return queryList[0] as QueryFormatFilter;
  }
  return { [QueryListOperandEnum.AND]: queryList as QueryMapFilter[] };
}
